import { useEffect } from "react";

import { useBreakpointRange } from "@newsela/angelou";
import PropTypes from "prop-types";

import useFocusManager from "@/view/utils/useFocusManager";
import StickyBar from "@/view/StickyBar";
import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import useReadAloudAudio from "@/view/ReadAloudControls/useReadAloudAudio";
import ReadAloudAudio from "./ReadAloudAudio";
import readAloudButtonsConfig from "./readAloudButtonsConfig";
import ReadAloudControlButton from "./ReadAloudControlButton";
import {
  rootCss,
  toggleAudioIconCss,
  controlsCss,
  restartIconCss,
} from "./ReadAloudControls.style";
import ReadAloudGraphic from "./ReadAloudGraphic";
import ReadAloudSpeedControl from "./ReadAloudSpeedControl";

const propTypes = {
  contentData: PropTypes.object,
  contentId: PropTypes.string,
  currentLevel: PropTypes.object,
  nextFocusRef: PropTypes.object,
};

const ReadAloudControls = ({
  contentData,
  contentId,
  currentLevel,
  nextFocusRef,
}) => {
  const currentRange = useBreakpointRange();

  const speech = useReadAloudAudio({
    content: contentData,
    selectedLevel: currentLevel,
  });

  const { setFocusManagerRef } = useFocusManager();

  const {
    isReadAloudModeActive,
    setIsReadAloudModeActive,
    readAloudContentMode,
    setReadAloudContentMode,
  } = useReadAloudMode();

  useEffect(() => {
    if (
      readAloudContentMode !== ReadAloudContentModes.ARTICLE &&
      isReadAloudModeActive &&
      speech.isPlaying
    ) {
      /* pause article read aloud when a quiz question or answer is being read aloud */
      speech.toggleAudio();
    }
  }, [readAloudContentMode]);

  if (!isReadAloudModeActive) return null;

  const handlePause = () => {
    speech.toggleAudio();
    setReadAloudContentMode(ReadAloudContentModes.ARTICLE);
  };
  const handleClose = () => {
    setIsReadAloudModeActive(false);
    requestAnimationFrame(() => {
      nextFocusRef?.current && nextFocusRef.current.focus();
    });
  };

  const sharedStreamProperties = {
    content_id: currentLevel?.id,
    contentType: "Article",
  };

  const { currentPlaybackRate, setCurrentPlaybackRate } = speech;
  const {
    close: closeButtonProps,
    jumpBack: jumpBackButtonProps,
    playPause: playPauseButtonProps,
    restart: restartButtonProps,
  } = readAloudButtonsConfig(
    speech,
    sharedStreamProperties,
    handleClose,
    setFocusManagerRef,
    handlePause,
  );

  const isQuizOrAnswerMode =
    readAloudContentMode === ReadAloudContentModes.QUIZ ||
    readAloudContentMode === ReadAloudContentModes.ANSWER;

  const isControlDisabled = {
    playPause: speech.isPausedForBlurredText,
    speed: isQuizOrAnswerMode,
    restart: isQuizOrAnswerMode,
    jumpback: isQuizOrAnswerMode,
  };

  return (
    <StickyBar>
      <div css={rootCss}>
        <ReadAloudAudio speech={speech} />
        <h6>Listen</h6>
        <div css={controlsCss}>
          <ReadAloudControlButton
            config={{
              ...playPauseButtonProps,
              disabled: isControlDisabled.playPause,
            }}
            iconCss={toggleAudioIconCss}
          />
          <ReadAloudSpeedControl
            contentId={contentId}
            currentLevelId={currentLevel?.id}
            currentPlaybackRate={currentPlaybackRate}
            headerContentId={currentLevel?.header_id}
            setCurrentPlaybackRate={setCurrentPlaybackRate}
            disabled={isControlDisabled.speed}
          />
          <ReadAloudControlButton
            config={{
              ...restartButtonProps,
              disabled: isControlDisabled.restart,
            }}
            iconCss={restartIconCss}
          />
          <ReadAloudControlButton
            config={{
              ...jumpBackButtonProps,
              disabled: isControlDisabled.jumpback,
            }}
            iconCss={restartIconCss}
          />
          {currentRange && <ReadAloudGraphic currentRange={currentRange} />}
        </div>
        <ReadAloudControlButton
          config={closeButtonProps}
          iconCss={toggleAudioIconCss}
        />
      </div>
    </StickyBar>
  );
};

ReadAloudControls.propTypes = propTypes;
export default ReadAloudControls;
